import { Fragment } from 'react';
import {
  BookOpen,
  Circle,
  CircleCheck,
  CircleHelp,
  CirclePlay,
  FileText,
  ListChecks,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@hrms/ui';
import {
  formatTrainingClock,
  formatTrainingDuration,
  type TrainingContentKind,
} from '@hrms/shared';

export type LessonRowState = 'done' | 'partial' | 'todo' | 'locked';

export interface LessonRowProps {
  kind: TrainingContentKind;
  title: string;
  durationSeconds?: number | null;
  pageCount?: number | null;
  questionCount?: number | null;
  state: LessonRowState;
  /** 0–100, drawn as the partial ring when `state` is `partial`. */
  percent?: number | null;
  /** The lesson open in the player: accent bar, "Now playing", `aria-current`. */
  current?: boolean;
  onSelect?: () => void;
  /** Defaults to true for a `locked` lesson (strict order), false otherwise. */
  disabled?: boolean;
  /**
   * The kinds of lesson that come with this one (a video's quiz, article, document…), shown as a
   * quiet "Quiz · Article" line under the title. Repeats collapse into a count ("2 Documents").
   */
  includes?: readonly TrainingContentKind[];
  className?: string;
}

const KIND_ICON: Record<TrainingContentKind, LucideIcon> = {
  video_url: CirclePlay,
  video_upload: CirclePlay,
  document: FileText,
  reading: BookOpen,
  checklist: ListChecks,
  quiz: CircleHelp,
};

const KIND_NAME: Record<TrainingContentKind, string> = {
  video_url: 'Video',
  video_upload: 'Video',
  document: 'Document',
  reading: 'Article',
  checklist: 'Checklist',
  quiz: 'Quiz',
};

const KIND_PLURAL: Record<TrainingContentKind, string> = {
  video_url: 'Videos',
  video_upload: 'Videos',
  document: 'Documents',
  reading: 'Articles',
  checklist: 'Checklists',
  quiz: 'Quizzes',
};

/** "Quiz", "2 Documents" — one entry per kind, in first-seen order. */
export function includedKinds(
  kinds: readonly TrainingContentKind[],
): { kind: TrainingContentKind; label: string }[] {
  const counts = new Map<string, { kind: TrainingContentKind; count: number }>();
  for (const kind of kinds) {
    const name = KIND_NAME[kind];
    const entry = counts.get(name);
    if (entry) entry.count += 1;
    else counts.set(name, { kind, count: 1 });
  }
  return [...counts.values()].map(({ kind, count }) => ({
    kind,
    label: count === 1 ? KIND_NAME[kind] : `${count} ${KIND_PLURAL[kind]}`,
  }));
}

/** The right-aligned length a video site shows: "4:12" for a video, "6 pages", "10 Q". */
export function lessonLengthLabel(
  kind: TrainingContentKind,
  durationSeconds?: number | null,
  pageCount?: number | null,
  questionCount?: number | null,
): string | null {
  switch (kind) {
    case 'video_url':
    case 'video_upload':
      return durationSeconds && durationSeconds > 0 ? formatTrainingClock(durationSeconds) : null;
    case 'document':
      return pageCount && pageCount > 0 ? `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}` : null;
    case 'quiz':
      return questionCount && questionCount > 0 ? `${questionCount} Q` : null;
    case 'reading':
    case 'checklist':
      return durationSeconds && durationSeconds > 0 ? formatTrainingDuration(durationSeconds) : null;
  }
}

function stateText(state: LessonRowState, percent: number): string {
  switch (state) {
    case 'done':
      return 'Completed';
    case 'partial':
      return `In progress, ${percent}%`;
    case 'todo':
      return 'Not started';
    case 'locked':
      return 'Locked — finish the earlier lessons first';
  }
}

/** A small partial ring in the progress tokens — the sidebar's "started" mark. */
function PartialRing({ percent }: { percent: number }) {
  const size = 18;
  const stroke = 2.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className="-rotate-90"
      aria-hidden="true"
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--progress-track)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--progress-fill)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        // A started lesson always shows a sliver, even at 0–4%, so it never looks "not started".
        strokeDashoffset={c * (1 - Math.max(8, percent) / 100)}
      />
    </svg>
  );
}

function StateIcon({ state, percent }: { state: LessonRowState; percent: number }) {
  switch (state) {
    case 'done':
      return <CircleCheck className="size-[18px] text-progress-fill" aria-hidden="true" />;
    case 'partial':
      return <PartialRing percent={percent} />;
    case 'todo':
      return <Circle className="size-[18px] text-fg-subtle" aria-hidden="true" />;
    case 'locked':
      return <Lock className="size-4 text-fg-subtle" aria-hidden="true" />;
  }
}

/**
 * One lesson in the course player's sidebar (PLAN §4.2, §10): state mark, type icon, title,
 * right-aligned tabular length. The lesson playing now gets an accent bar on its leading edge,
 * "Now playing" and `aria-current`.
 *
 * A real `<button>` when `onSelect` is given, so Tab / Enter / Space work with no extra key
 * handling; a plain row otherwise (HR's read-only outline). The state is announced in words
 * ("Completed", "In progress, 40%") — the tick and ring are only its picture.
 */
export function LessonRow({
  kind,
  title,
  durationSeconds,
  pageCount,
  questionCount,
  state,
  percent,
  current = false,
  onSelect,
  disabled,
  includes,
  className,
}: LessonRowProps) {
  const pct = Math.round(Math.min(100, Math.max(0, Number.isFinite(percent) ? (percent ?? 0) : 0)));
  const isDisabled = disabled ?? state === 'locked';
  const length = lessonLengthLabel(kind, durationSeconds, pageCount, questionCount);
  const KindIcon = KIND_ICON[kind];
  const extras = includes && includes.length > 0 ? includedKinds(includes) : [];

  const content = (
    <>
      {current && (
        <span aria-hidden="true" className="absolute inset-y-0 start-0 w-1 rounded-e-full bg-accent" />
      )}
      <span className="flex size-5 shrink-0 items-center justify-center">
        <StateIcon state={state} percent={pct} />
      </span>
      <KindIcon
        className={cn('size-4 shrink-0', current ? 'text-accent' : 'text-fg-muted')}
        aria-hidden="true"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'line-clamp-2 text-sm leading-snug',
            current ? 'font-semibold text-fg' : 'text-fg',
            state === 'locked' && 'text-fg-muted',
          )}
        >
          {title}
        </span>
        {current && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-accent">
            Now playing
          </span>
        )}
        {extras.length > 0 && (
          <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
            <span className="sr-only">Includes </span>
            {extras.map(({ kind: extraKind, label }, index) => {
              const ExtraIcon = KIND_ICON[extraKind];
              return (
                <Fragment key={label}>
                  {index > 0 && <span aria-hidden="true">·</span>}
                  <span className="inline-flex items-center gap-1">
                    <ExtraIcon className="size-3 shrink-0" aria-hidden="true" />
                    {label}
                  </span>
                </Fragment>
              );
            })}
          </span>
        )}
      </span>
      {length && (
        <span className="shrink-0 text-xs tabular-nums text-fg-muted">{length}</span>
      )}
      <span className="sr-only">
        {`, ${KIND_NAME[kind]}, ${stateText(state, pct)}`}
      </span>
    </>
  );

  const rowClass = cn(
    'relative flex min-h-14 w-full items-center gap-3 px-4 py-2 text-start',
    current ? 'bg-accent-subtle' : 'bg-transparent',
    className,
  );

  if (!onSelect) {
    return (
      <div className={rowClass} aria-current={current ? 'true' : undefined}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isDisabled}
      aria-current={current ? 'true' : undefined}
      className={cn(
        rowClass,
        'transition-colors duration-150',
        !current && 'hover:bg-bg-subtle',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed disabled:hover:bg-transparent',
      )}
    >
      {content}
    </button>
  );
}
